import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './TooltipProvider.css';

const DELAY_MS = 300;
const GAP = 8;
const TIP_ID = 'app-tooltip';

export default function TooltipProvider({ children }) {
  const tipRef = useRef(null);
  const timerRef = useRef(null);
  const currentTarget = useRef(null);

  const show = useCallback((el) => {
    const tip = tipRef.current;
    if (!tip) return;

    const text = el.getAttribute('data-tooltip');
    if (!text) return;

    tip.textContent = text;
    currentTarget.current = el;
    // Expose the tooltip text to assistive tech: point the active element at the
    // shared tooltip node while it's shown (removed again in hide()). Without
    // this, the 400+ data-tooltip strings are invisible to screen readers, and
    // for many controls the tooltip is the only description they have.
    el.setAttribute('aria-describedby', TIP_ID);

    const pos = el.getAttribute('data-tooltip-pos') || 'top';
    const rect = el.getBoundingClientRect();
    // Measure tip dimensions
    tip.style.visibility = 'hidden';
    tip.style.display = 'block';
    tip.className = 'tooltip-portal';
    tip.setAttribute('data-pos', pos);
    const tipRect = tip.getBoundingClientRect();

    let top, left;
    let finalPos = pos;

    // Calculate position, flipping if needed
    if (pos === 'top' || pos === 'bottom') {
      left = rect.left + rect.width / 2 - tipRect.width / 2;
      if (pos === 'top') {
        top = rect.top - tipRect.height - GAP;
        if (top < 4) { top = rect.bottom + GAP; finalPos = 'bottom'; }
      } else {
        top = rect.bottom + GAP;
        if (top + tipRect.height > window.innerHeight - 4) { top = rect.top - tipRect.height - GAP; finalPos = 'top'; }
      }
      // Clamp horizontal
      if (left < 4) left = 4;
      if (left + tipRect.width > window.innerWidth - 4) left = window.innerWidth - tipRect.width - 4;
    } else {
      top = rect.top + rect.height / 2 - tipRect.height / 2;
      if (pos === 'left') {
        left = rect.left - tipRect.width - GAP;
        if (left < 4) { left = rect.right + GAP; finalPos = 'right'; }
      } else {
        left = rect.right + GAP;
        if (left + tipRect.width > window.innerWidth - 4) { left = rect.left - tipRect.width - GAP; finalPos = 'left'; }
      }
      // Clamp vertical
      if (top < 4) top = 4;
      if (top + tipRect.height > window.innerHeight - 4) top = window.innerHeight - tipRect.height - 4;
    }

    tip.setAttribute('data-pos', finalPos);
    tip.style.top = `${top}px`;
    tip.style.left = `${left}px`;
    tip.style.visibility = '';
    tip.classList.add('visible');
  }, []);

  const hide = useCallback(() => {
    clearTimeout(timerRef.current);
    // Drop the describedby link so AT doesn't reference the now-stale/hidden tip.
    if (currentTarget.current?.getAttribute('aria-describedby') === TIP_ID) {
      currentTarget.current.removeAttribute('aria-describedby');
    }
    currentTarget.current = null;
    const tip = tipRef.current;
    if (tip) {
      tip.classList.remove('visible');
    }
  }, []);

  useEffect(() => {
    const findTooltipTarget = (node) => {
      while (node && node !== document.body) {
        if (node.hasAttribute && node.hasAttribute('data-tooltip')) return node;
        node = node.parentElement;
      }
      return null;
    };

    const onEnter = (e) => {
      const target = findTooltipTarget(e.target);
      if (!target) return;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => show(target), DELAY_MS);
    };

    const onLeave = (e) => {
      const target = findTooltipTarget(e.target);
      if (!target) return;
      // Only hide if we're leaving the current tooltip target
      if (currentTarget.current === target || !currentTarget.current) {
        hide();
      }
    };

    const onScroll = () => hide();

    // Keyboard parity: reveal the tooltip when a target receives focus and
    // hide it when focus leaves, mirroring the mouseover/mouseout behavior.
    const onFocusIn = (e) => onEnter(e);
    const onFocusOut = (e) => onLeave(e);

    document.addEventListener('mouseover', onEnter, true);
    document.addEventListener('mouseout', onLeave, true);
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    document.addEventListener('scroll', onScroll, true);

    return () => {
      clearTimeout(timerRef.current);
      document.removeEventListener('mouseover', onEnter, true);
      document.removeEventListener('mouseout', onLeave, true);
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [show, hide]);

  return (
    <>
      {children}
      {createPortal(
        <div ref={tipRef} className="tooltip-portal" id={TIP_ID} role="tooltip" />,
        document.body
      )}
    </>
  );
}
